// Web: build the kit (the PDF library loads only now), then open it, save it into a folder the
// user picks (so it can sit inside iCloud Drive / Google Drive / OneDrive / Dropbox), hand it to the
// phone's share sheet ("Save to Files"), or download everything as one tidy ZIP.
import type { KitInput } from '../logic/recoveryKit';

export type Kit = { folder: string; files: { name: string; data: Uint8Array; type: string }[]; zip: () => Uint8Array };

export const appOrigin = window.location.origin;

type DirHandle = {
  getDirectoryHandle(name: string, o: { create: boolean }): Promise<DirHandle>;
  getFileHandle(name: string, o: { create: boolean }): Promise<{ createWritable(): Promise<{ write(d: BlobPart): Promise<void>; close(): Promise<void> }> }>;
};
const picker = (window as unknown as { showDirectoryPicker?: (o: { mode: 'readwrite' }) => Promise<DirHandle> }).showDirectoryPicker;

/** File System Access API: desktop Chrome, Edge and Opera. */
export const canSaveToFolder = typeof picker === 'function';

const blob = (data: Uint8Array, type: string) => new Blob([data as BlobPart], { type });
const asFiles = (kit: Kit) => kit.files.map(f => new File([f.data as BlobPart], f.name, { type: f.type }));

export async function makeKit(input: KitInput): Promise<Kit> {
  return (await import('../logic/recoveryKit')).buildKit(input);
}

/** Opens the PDF in a new tab. False if the browser blocked the window (no recent tap). */
export function openPdf(kit: Kit): boolean {
  const pdf = kit.files.find(f => f.type === 'application/pdf');
  if (!pdf) return false;
  const url = URL.createObjectURL(blob(pdf.data, pdf.type));
  const w = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
  return !!w;
}

export function downloadZip(kit: Kit): void {
  const url = URL.createObjectURL(blob(kit.zip(), 'application/zip'));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${kit.folder}.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Writes a folder with the kit's files into a folder the user picks. False if they cancelled. */
export async function saveToFolder(kit: Kit): Promise<boolean> {
  if (!picker) return false;
  try {
    const parent = await picker.call(window, { mode: 'readwrite' });
    const dir = await parent.getDirectoryHandle(kit.folder, { create: true });
    for (const f of kit.files) {
      const w = await (await dir.getFileHandle(f.name, { create: true })).createWritable();
      await w.write(f.data as BlobPart);
      await w.close();
    }
    return true;
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError') return false;
    throw e;
  }
}

export const canShare = (kit: Kit): boolean => typeof navigator.canShare === 'function' && typeof navigator.share === 'function' && navigator.canShare({ files: asFiles(kit) });

/** The phone's share sheet: Save to Files, Google Drive, iCloud Drive, AirDrop, print... */
export async function shareFiles(kit: Kit): Promise<boolean> {
  try {
    await navigator.share({ files: asFiles(kit), title: kit.folder });
    return true;
  } catch (e) {
    if ((e as { name?: string }).name === 'AbortError') return false;
    throw e;
  }
}
